const MUSTACHE_FULL = /^\s*\{\{([\s\S]+)\}\}\s*$/;
const MUSTACHE_ANY = /\{\{([\s\S]+?)\}\}/g;
/**
 * Resolve `{{ ... }}` templates against `context.variables` (and a few built-ins).
 *
 * - `{{test}}` → variables.get('test')
 * - `{{test * 2}}` → evaluated as a JS expression with variable bindings
 * - `{{formvalues.adjuster_name}}` → current SurveyJS field value (also `formValues`)
 * - `{{fields[2].value}}` / `{{fields[2].name}}` → 3rd question in form order
 * - `{{context.taskId}}` / `{{taskId}}` → host identity seeded at FAF init
 * - `{{context.pageUrl}}` / `{{pageUrl}}` → page URL
 * - When the whole string is a single mustache, the typed result is returned
 *   (number/object/boolean), not stringified.
 * - Mixed text (`Hello {{name}}`) always returns a string.
 */
export function resolveTemplate(input, context) {
    if (typeof input !== 'string')
        return input;
    if (!input.includes('{{'))
        return input;
    const full = input.match(MUSTACHE_FULL);
    if (full) {
        return evaluateExpression(full[1].trim(), context);
    }
    return input.replace(MUSTACHE_ANY, (_match, expr) => {
        const result = evaluateExpression(expr.trim(), context);
        if (result == null)
            return '';
        if (typeof result === 'object')
            return JSON.stringify(result);
        return String(result);
    });
}
/** Resolve every string leaf in a config object (shallow + one-level nested objects/arrays). */
export function resolveConfigStrings(config, context) {
    const out = {};
    for (const [key, value] of Object.entries(config)) {
        out[key] = resolveDeep(value, context);
    }
    return out;
}
function resolveDeep(value, context) {
    if (typeof value === 'string')
        return resolveTemplate(value, context);
    if (Array.isArray(value)) {
        return value.map((item) => resolveDeep(item, context));
    }
    if (value && typeof value === 'object') {
        const obj = {};
        for (const [k, v] of Object.entries(value)) {
            obj[k] = resolveDeep(v, context);
        }
        return obj;
    }
    return value;
}
function evaluateExpression(expr, context) {
    if (!expr)
        return undefined;
    const bindings = buildBindings(context);
    const names = Object.keys(bindings);
    const values = Object.values(bindings);
    try {
        // Temporary admin tooling — expressions are authored by trusted operators.
        // eslint-disable-next-line no-new-func
        const fn = new Function(...names, `"use strict"; return (${expr});`);
        return fn(...values);
    }
    catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        context.log(`[template] failed to evaluate {{${expr}}}: ${message}`);
        throw new Error(`Template expression failed: {{${expr}}} (${message})`);
    }
}
function buildBindings(context) {
    const formValues = getLiveFormValues(context);
    const fields = getOrderedFormFields(context, formValues);
    const host = {
        formId: context.formId ?? null,
        incidentId: context.incidentId ?? null,
        taskId: context.taskId ?? null,
        userId: context.userId ?? null,
        orgId: context.orgId ?? context.organizationId ?? null,
        pageUrl: context.pageUrl ?? null,
        milestone: context.milestone ?? null,
        change: context.change ?? null,
    };
    const bindings = {
        // Built-ins available in every expression
        form: context.form ?? null,
        formValues,
        // Lowercase alias so {{formvalues.adjuster_name}} works as authored.
        formvalues: formValues,
        // Ordered question list: {{fields[2].value}}, {{fields[2].name}}
        fields,
        // Explicit namespace: {{context.taskId}}, {{context.pageUrl}}, …
        context: host,
        ...host,
        variables: Object.fromEntries(context.variables.entries()),
    };
    // Flat-map simple identifier keys from context.variables (e.g. test → {{test}})
    // Do not override host identity keys.
    for (const [key, value] of context.variables.entries()) {
        if (isValidIdentifier(key) && !(key in bindings)) {
            bindings[key] = value;
        }
    }
    return bindings;
}
/** Prefer live SurveyJS `.data` when present; fall back to the context snapshot. */
function getLiveFormValues(context) {
    const form = context.form;
    if (form?.data && typeof form.data === 'object' && !Array.isArray(form.data)) {
        return form.data;
    }
    return context.formValues ?? {};
}
/**
 * Ordered form fields for template access (`fields[n]`).
 * Prefer SurveyJS question order; fall back to Object.keys(formValues) order.
 */
export function getOrderedFormFields(context, formValues = getLiveFormValues(context)) {
    const model = context.form;
    const questions = model?.getAllQuestions?.(false, false, true);
    if (Array.isArray(questions) && questions.length > 0) {
        return questions
            .filter((q) => typeof q?.name === 'string' && q.name.length > 0)
            .map((q, index) => {
            const name = q.name;
            const value = name in formValues ? formValues[name] : q.value;
            return {
                index,
                name,
                value,
                title: typeof q.title === 'string' ? q.title : null,
                visible: typeof q.visible === 'boolean' ? q.visible : null,
            };
        });
    }
    return Object.keys(formValues).map((name, index) => ({
        index,
        name,
        value: formValues[name],
        title: null,
        visible: null,
    }));
}
function isValidIdentifier(name) {
    return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(name);
}
